import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import './LoginPage.css';

const LoginPage = () => {
  const API_URL = useMemo(() => {
  const isLocalHost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname.startsWith("192.168.");

  const envApiUrl = process.env.REACT_APP_API_URL?.trim();

  return envApiUrl && (isLocalHost || (!envApiUrl.includes("localhost") && !envApiUrl.includes("127.0.0.1"))) ? envApiUrl : "/api";
}, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('userToken'));
  const navigate = useNavigate();
  const [partsList, setPartsList] = useState([]);
  const [activeFormTab, setActiveFormTab] = useState('parts');
  const [categoriesList, setCategoriesList] = useState([]);
  const [productsList, setProductsList] = useState([]);

  // Parts Order Form States
  const [partsFormData, setPartsFormData] = useState({
    dealerName: '',
    contactNumber: '',
    partNameOrNumber: '',
    quantity: '',
    machineModel: '',
    deliveryAddress: ''
  });

  // Stock Request Form States
  const [stockFormData, setStockFormData] = useState({
    dealerName: '',
    district: '',
    productCategory: '',
    productName: '',
    quantityRequired: '',
    requiredByDate: '',
    additionalNotes: '',
    contactNumber: ''
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setToken(localStorage.getItem('userToken'));

    const fetchDropdowns = async () => {
      try {
        const [partsRes, catsRes, prodsRes] = await Promise.all([
          fetch(`${API_URL}/parts`),
          fetch(`${API_URL}/categories`),
          fetch(`${API_URL}/products?limit=1000`)
        ]);
        const partsJson = await partsRes.json();
        const catsJson = await catsRes.json();
        const prodsJson = await prodsRes.json();
        if (partsJson.success && partsJson.data) {
          setPartsList(partsJson.data);
        }
        if (catsJson.success && catsJson.data) {
          setCategoriesList(catsJson.data);
        }
        if (prodsJson.success && prodsJson.data) {
          setProductsList(prodsJson.data);
        }
      } catch (err) {
        console.error('Error fetching dropdown data:', err);
      }
    };
    fetchDropdowns();
  }, [API_URL]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        localStorage.setItem('userToken', data.token);
        localStorage.setItem('userEmail', data.user.email);
        setToken(data.token);
      } else {
        alert(data.message || 'Invalid email or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      const defaultEmail = process.env.REACT_APP_DEFAULT_EMAIL || 'user@kisaanmaan.com';
      const defaultPass = process.env.REACT_APP_DEFAULT_PASSWORD || 'user@12345';
      
      if (email === defaultEmail && password === defaultPass) {
        localStorage.setItem('userToken', 'mock_user_token_123');
        localStorage.setItem('userEmail', email);
        setToken('mock_user_token_123');
      } else {
        alert('Could not connect to the authentication server. Please check your credentials or connection.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    const emailPrompt = prompt('Enter your registered email address to reset password:');
    if (emailPrompt) {
      alert(`A password reset link has been sent to: ${emailPrompt}`);
    }
  };

  const handlePartsFormChange = (field, value) => {
    setPartsFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePartsSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/dealer-support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requestType: 'Parts Orders',
          dealerName: partsFormData.dealerName,
          contactNumber: partsFormData.contactNumber,
          partNameOrNumber: partsFormData.partNameOrNumber,
          quantity: parseInt(partsFormData.quantity) || 0,
          machineModel: partsFormData.machineModel,
          deliveryAddress: partsFormData.deliveryAddress
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        alert('Failed to submit parts order request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting parts order request:', error);
      alert('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStockFormChange = (field, value) => {
    setStockFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleStockSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/dealer-support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requestType: 'Stock Requests',
          dealerName: stockFormData.dealerName,
          district: stockFormData.district,
          productCategory: stockFormData.productCategory,
          productName: stockFormData.productName,
          quantityRequired: parseInt(stockFormData.quantityRequired) || 0,
          requiredByDate: stockFormData.requiredByDate,
          additionalNotes: stockFormData.additionalNotes,
          contactNumber: stockFormData.contactNumber || 'N/A'
        })
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        alert('Failed to submit stock request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting stock request:', error);
      alert('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userEmail');
    setToken(null);
    setSubmitted(false);
    setPartsFormData({
      dealerName: '',
      contactNumber: '',
      partNameOrNumber: '',
      quantity: '',
      machineModel: '',
      deliveryAddress: ''
    });
    setStockFormData({
      dealerName: '',
      district: '',
      productCategory: '',
      productName: '',
      quantityRequired: '',
      requiredByDate: '',
      additionalNotes: '',
      contactNumber: ''
    });
  };

  return (
    <div className="login-page-root" style={token ? { backgroundColor: '#ffffff' } : {}}>
      <Header />
      <div className="login-page-container" style={token ? { backgroundColor: '#ffffff', minHeight: 'auto', padding: '60px 20px 200px 20px' } : {}}>
        {!token && <div className="login-card-blur"></div>}
        
        {token ? (
          <div className="parts-order-clean-container" style={{ width: '100%', maxWidth: '600px', margin: '0 auto', background: '#ffffff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
              <div>
                <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#0f172a', margin: 0 }}>
                  {activeFormTab === 'parts' ? 'Parts Order' : 'Machine Order'}
                </h1>
                <p style={{ color: '#64748b', fontSize: '14px', margin: '4px 0 0 0' }}>
                  {activeFormTab === 'parts' 
                    ? 'Order genuine spare parts directly from Kisaan Maan warehouse.' 
                    : 'Submit a request for farm machine orders from Kisaan Maan.'}
                </p>
              </div>
              <button 
                onClick={handleLogout} 
                style={{ 
                  background: '#f1f5f9', 
                  color: '#64748b', 
                  border: 'none', 
                  padding: '8px 16px', 
                  borderRadius: '8px', 
                  fontWeight: '600', 
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                Logout
              </button>
            </div>

            {/* TAB NAVIGATION */}
            <div className="tab-navigation" style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '2px solid #f1f5f9' }}>
              <button 
                type="button"
                onClick={() => { setActiveFormTab('parts'); setSubmitted(false); }}
                style={{ 
                  padding: '12px 20px', 
                  background: 'none', 
                  border: 'none', 
                  borderBottom: activeFormTab === 'parts' ? '3px solid #e8762e' : '3px solid transparent',
                  fontWeight: '700',
                  color: activeFormTab === 'parts' ? '#e8762e' : '#64748b',
                  cursor: 'pointer',
                  fontSize: '15px',
                  outline: 'none'
                }}
              >
                Parts Order Form
              </button>
              <button 
                type="button"
                onClick={() => { setActiveFormTab('stock'); setSubmitted(false); }}
                style={{ 
                  padding: '12px 20px', 
                  background: 'none', 
                  border: 'none', 
                  borderBottom: activeFormTab === 'stock' ? '3px solid #4ba242' : '3px solid transparent',
                  fontWeight: '700',
                  color: activeFormTab === 'stock' ? '#4ba242' : '#64748b',
                  cursor: 'pointer',
                  fontSize: '15px',
                  outline: 'none'
                }}
              >
                Machine Order Form
              </button>
            </div>

            {submitted ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ color: activeFormTab === 'parts' ? '#e8762e' : '#4ba242', marginBottom: '16px' }}>
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto' }}>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>Request Submitted</h3>
                <p style={{ color: '#64748b', fontSize: '15px', marginBottom: '24px' }}>
                  Your {activeFormTab === 'parts' ? 'spare parts order' : 'machine order'} request has been submitted successfully.
                </p>
                <button 
                  className="login-btn-primary" 
                  style={{ background: activeFormTab === 'parts' ? '#e8762e' : '#4ba242', maxWidth: '200px', margin: '0 auto' }} 
                  onClick={() => setSubmitted(false)}
                >
                  Submit Another
                </button>
              </div>
            ) : activeFormTab === 'parts' ? (
              <form onSubmit={handlePartsSubmit} className="login-form">
                <div className="form-group">
                  <label>Dealer Name</label>
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="Your dealership name"
                      value={partsFormData.dealerName}
                      onChange={e => handlePartsFormChange('dealerName', e.target.value)}
                      required
                      style={{ paddingLeft: '16px' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Contact Number</label>
                  <div className="input-group">
                    <input
                      type="tel"
                      placeholder="+91 XXXXXXXXXX"
                      value={partsFormData.contactNumber}
                      onChange={e => handlePartsFormChange('contactNumber', e.target.value)}
                      required
                      style={{ paddingLeft: '16px' }}
                    />
                  </div>
                </div>

                 <div className="form-group">
                  <label>Part Name / Part Number</label>
                  <div className="input-group">
                    <select
                      className="ds-input"
                      value={partsFormData.partNameOrNumber}
                      onChange={e => handlePartsFormChange('partNameOrNumber', e.target.value)}
                      required
                      style={{ width: '100%', padding: '14px 16px', border: '1.5px solid #cbd5e1', borderRadius: '12px', fontSize: '15px', outline: 'none' }}
                    >
                      <option value="">— Select —</option>
                      {partsList && partsList.length > 0 ? (
                        <>
                          {partsList.map(p => {
                            const val = `${p.modelNo} (${p.materialCode})`;
                            return <option key={p._id} value={val}>{val}</option>;
                          })}
                          <option value="Other / Not Listed">Other / Not Listed</option>
                        </>
                      ) : (
                        <option value="Other / Not Listed">Other / Not Listed</option>
                      )}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Quantity</label>
                  <div className="input-group">
                    <input
                      type="number"
                      placeholder="e.g. 10"
                      value={partsFormData.quantity}
                      onChange={e => handlePartsFormChange('quantity', e.target.value)}
                      required
                      style={{ paddingLeft: '16px' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Delivery Address</label>
                  <div className="input-group">
                    <textarea
                      placeholder="Full delivery address..."
                      value={partsFormData.deliveryAddress}
                      onChange={e => handlePartsFormChange('deliveryAddress', e.target.value)}
                      required
                      style={{ width: '100%', padding: '14px 16px', border: '1.5px solid #cbd5e1', borderRadius: '12px', fontSize: '15px', outline: 'none', minHeight: '100px', resize: 'vertical' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                  <button 
                    type="button" 
                    style={{ 
                      flex: 1, 
                      padding: '14px', 
                      background: '#f1f5f9', 
                      color: '#475569', 
                      border: 'none', 
                      borderRadius: '12px', 
                      fontSize: '16px', 
                      fontWeight: '700', 
                      cursor: 'pointer' 
                    }}
                    onClick={() => navigate('/')}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    style={{ 
                      flex: 1, 
                      padding: '14px', 
                      background: '#e8762e', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '12px', 
                      fontSize: '16px', 
                      fontWeight: '700', 
                      cursor: 'pointer' 
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleStockSubmit} className="login-form">
                <div className="form-group">
                  <label>Dealer Name</label>
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="Your dealership name"
                      value={stockFormData.dealerName}
                      onChange={e => handleStockFormChange('dealerName', e.target.value)}
                      required
                      style={{ paddingLeft: '16px' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Contact Number</label>
                  <div className="input-group">
                    <input
                      type="tel"
                      placeholder="+91 XXXXXXXXXX"
                      value={stockFormData.contactNumber}
                      onChange={e => handleStockFormChange('contactNumber', e.target.value)}
                      style={{ paddingLeft: '16px' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>District</label>
                  <div className="input-group">
                    <select
                      value={stockFormData.district}
                      onChange={e => handleStockFormChange('district', e.target.value)}
                      required
                      style={{ width: '100%', padding: '14px 16px', border: '1.5px solid #cbd5e1', borderRadius: '12px', fontSize: '15px', outline: 'none' }}
                    >
                      <option value="">— Select —</option>
                      {['Muzaffarpur', 'Sitamarhi', 'Madhubani', 'Bhagalpur', 'Khagaria', 'Lakhisarai', 'Garkha', 'Darbhanga', 'Lalganj', 'Gopalganj', 'Siwan', 'Rosera', 'Bihia', 'Begusarai'].map(dist => (
                        <option key={dist} value={dist}>{dist}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Product Category</label>
                  <div className="input-group">
                    <select
                      value={stockFormData.productCategory}
                      onChange={e => {
                        handleStockFormChange('productCategory', e.target.value);
                        handleStockFormChange('productName', ''); // Reset product on category change
                      }}
                      required
                      style={{ width: '100%', padding: '14px 16px', border: '1.5px solid #cbd5e1', borderRadius: '12px', fontSize: '15px', outline: 'none' }}
                    >
                      <option value="">— Select —</option>
                      {categoriesList && categoriesList.length > 0 ? (
                        categoriesList.map(cat => (
                          <option key={cat._id} value={cat.name}>{cat.name}</option>
                        ))
                      ) : (
                        ['Tillage', 'Sowing', 'Harvesting', 'Crop Protection', 'Post-Harvest', 'Material Handling', 'Power Equipment'].map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                {stockFormData.productCategory && (
                  <div className="form-group">
                    <label>Select Machine / Product</label>
                    <div className="input-group">
                      <select
                        value={stockFormData.productName}
                        onChange={e => handleStockFormChange('productName', e.target.value)}
                        required
                        style={{ width: '100%', padding: '14px 16px', border: '1.5px solid #cbd5e1', borderRadius: '12px', fontSize: '15px', outline: 'none' }}
                      >
                        <option value="">— Select —</option>
                        {productsList
                          .filter(p => {
                            if (!p.category) return false;
                            const catName = typeof p.category === 'object' 
                              ? p.category.name 
                              : (categoriesList.find(c => c._id === p.category) || {}).name;
                            return catName === stockFormData.productCategory;
                          })
                          .map(prod => (
                            <option key={prod._id} value={prod.name}>{prod.name}</option>
                          ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>Quantity Required</label>
                  <div className="input-group">
                    <input
                      type="number"
                      placeholder="e.g. 5"
                      value={stockFormData.quantityRequired}
                      onChange={e => handleStockFormChange('quantityRequired', e.target.value)}
                      required
                      style={{ paddingLeft: '16px' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Required By Date</label>
                  <div className="input-group">
                    <input
                      type="date"
                      value={stockFormData.requiredByDate}
                      onChange={e => handleStockFormChange('requiredByDate', e.target.value)}
                      required
                      style={{ padding: '14px 16px', border: '1.5px solid #cbd5e1', borderRadius: '12px', fontSize: '15px', outline: 'none', width: '100%' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Additional Notes</label>
                  <div className="input-group">
                    <textarea
                      placeholder="Any specific model or specification..."
                      value={stockFormData.additionalNotes}
                      onChange={e => handleStockFormChange('additionalNotes', e.target.value)}
                      style={{ width: '100%', padding: '14px 16px', border: '1.5px solid #cbd5e1', borderRadius: '12px', fontSize: '15px', outline: 'none', minHeight: '100px', resize: 'vertical' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                  <button 
                    type="button" 
                    style={{ 
                      flex: 1, 
                      padding: '14px', 
                      background: '#f1f5f9', 
                      color: '#475569', 
                      border: 'none', 
                      borderRadius: '12px', 
                      fontSize: '16px', 
                      fontWeight: '700', 
                      cursor: 'pointer' 
                    }}
                    onClick={() => navigate('/')}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    style={{ 
                      flex: 1, 
                      padding: '14px', 
                      background: '#4ba242', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '12px', 
                      fontSize: '16px', 
                      fontWeight: '700', 
                      cursor: 'pointer' 
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Submitting...' : 'Submit Request'}
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <div className="login-card">
            <div className="login-card-header">
              <h2>Sign In</h2>
              <p>Welcome back! Access your account details</p>
            </div>
            <form onSubmit={handleLogin} className="login-form">
              <div className="form-group">
                <label htmlFor="email">Email / Username</label>
                <div className="input-group">
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path><polyline points="22,6 12,13 2,6"></polyline></svg>
                  </span>
                  <input
                    type="email"
                    id="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <div className="label-row">
                  <label htmlFor="password">Password</label>
                  <a href="#forgot" className="forgot-link" onClick={handleForgotPassword}>Forgot Password?</a>
                </div>
                <div className="input-group">
                  <span className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                    )}
                  </button>
                </div>
              </div>

              <button type="submit" className="login-btn-primary" disabled={isLoading}>
                {isLoading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default LoginPage;


